import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import apiClient from '../../config/axios';
import displayFormatText from '../../utils/displayFormat';
import { getFamily } from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';

/**
 * "To qualify" — the flattened prerequisite checklist (issue #201), computed
 * server-side (`GET /api/<segment>/:slug/qualify`, server/routes/qualify.js):
 * the transitive closure of the Family's self-referencing prerequisite
 * reference merged to distinct leaves, deepest-first, so a reader sees what
 * they actually need — and in what order — without walking the chain page by
 * page.
 *
 * It is PAGE CONTENT below the detail body, not an action-row control (#287),
 * modeled on `SimilarRail`: fetched lazily AFTER the detail rendered, and it
 * renders NOTHING while loading, on error, or on `{ qualify: null }` — a
 * Family with no closure edge (PF1 feats, classes, any user Family) simply
 * has no panel, and a closure that would only restate the page's own
 * prerequisites group is null by the server's rule, not hidden by a client
 * guess.
 *
 * A special prerequisite is spelled through `displayFormatText`, the SAME
 * seam the detail section above uses — so the panel reads "Base attack bonus
 * +8" and never "BAB +x 8". The fields to interpolate come from the
 * response's own `link_data` declaration, so a Family the bundle has never
 * seen (a user-authored system served through the runtime registry) spells
 * itself the same way. Row links follow the nav contract (`state.from` /
 * `state.listFrom`), and a row without a slug or a routable segment renders
 * as plain text, never as a dead link (the link-integrity rule).
 */

// A minimal attribute stand-in for the two shared spelling seams: both only
// read `linkData[].key` in declared order, which the response carries.
const attrFor = (group) => ({ linkData: (group.link_data || []).map((f) => ({ key: f.key })) });

/** The `linkDataText` rule (DetailSection): first field is the value, the
 *  rest are qualifiers. Re-derived from the response's link_data here because
 *  the panel may render a Family the client registry has never seen. */
function dataText(group, row) {
  const fields = (group.link_data || []).filter((f) => row[f.key] !== undefined
    && row[f.key] !== null && row[f.key] !== '');
  if (!fields.length) return '';
  const [primary, ...qualifiers] = fields;
  return `${row[primary.key]}${qualifiers.map((f) => ` (${row[f.key]})`).join('')}`;
}

function RowName({ row, nav }) {
  if (row.entity_type && row.slug) {
    return (
      <Link
        to={`/${row.entity_type}?slug=${encodeURIComponent(row.slug)}`}
        state={nav}
        className="footer-link"
      >
        {row.name}
      </Link>
    );
  }
  return <span>{row.name}</span>;
}

/**
 * The client half of the server's closure rule (routes/qualify.js): a Family
 * has a closure edge when a `many` reference attribute in a declared group
 * targets the Family itself. A Family without one — every spell, monster,
 * item, class — always answered `{ qualify: null }`, and every detail page
 * paid a request to learn it (API-call budget, 2026-09-07). The registry
 * already says so, so the request is made only where an answer is possible.
 */
export function hasClosureEdge(family) {
  if (!family || !Array.isArray(family.attributes)) return false;
  return family.attributes.some((attr) => (
    attr.kind === 'reference' && attr.many && attr.groupKey
    && attr.targetFamily && attr.targetFamily === family.family
  ));
}

export default function QualifyPanel({ segment, slug, from, listFrom }) {
  const [qualify, setQualify] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setQualify(null);
    if (!slug) return undefined;
    if (!hasClosureEdge(getFamily(segment, getActiveSystem()))) return undefined;
    apiClient
      .get(`/api/${segment}/${encodeURIComponent(slug)}/qualify`)
      .then((res) => { if (!cancelled) setQualify(res.data?.qualify || null); })
      .catch(() => { if (!cancelled) setQualify(null); });
    return () => { cancelled = true; };
  }, [segment, slug]);

  if (!qualify) return null;
  const { nodes = [], requirements = [], text = [] } = qualify;
  const nav = { from, listFrom };
  const family = getFamily(segment, getActiveSystem());
  const plural = family?.labels?.plural || 'Entries';
  const muted = { color: 'var(--text-muted)' };

  return (
    <div className="card" data-testid="qualify-panel">
      <h2 className="section-header flex items-center gap-2">
        <ListChecks className="w-5 h-5" style={{ color: '#C9A84C' }} aria-hidden="true" />
        <span>To Qualify</span>
      </h2>
      {nodes.length > 0 && (
        <div className="mt-2">
          <h3 className="text-sm font-semibold">{plural} to acquire, in order</h3>
          <ol className="mt-1 list-decimal list-inside space-y-0.5">
            {nodes.map((node) => (
              <li key={node.slug}>
                <Link
                  to={`/${segment}?slug=${encodeURIComponent(node.slug)}`}
                  state={nav}
                  className="footer-link"
                >
                  {node.name}
                </Link>
                {node.notes && node.notes.length > 0 && (
                  <span className="text-sm" style={muted}> ({node.notes.join('; ')})</span>
                )}
                {!node.direct && (
                  <span className="text-xs" style={muted}> — needed for a later step</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
      {requirements.map((group) => (
        <div className="mt-3" key={group.key}>
          <h3 className="text-sm font-semibold">{group.label || group.key}</h3>
          {group.rows && (
            <ul className="mt-1 space-y-0.5">
              {group.rows.map((row, i) => {
                const spelled = displayFormatText(attrFor(group), row);
                if (spelled !== null) {
                  return <li key={`${row.slug}-${i}`}>{spelled}</li>;
                }
                const data = dataText(group, row);
                return (
                  <li key={`${row.slug}-${i}`}>
                    <RowName row={row} nav={nav} />
                    {data && <span>{`: ${data}`}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {group.grouped && (
            <ul className="mt-1 space-y-0.5">
              {group.grouped.map((orList, i) => (
                <li key={`${orList.source.slug}-${i}`}>
                  {orList.rows.map((row, j) => (
                    <React.Fragment key={row.slug || j}>
                      {j > 0 && <span> or </span>}
                      <RowName row={row} nav={nav} />
                    </React.Fragment>
                  ))}
                  <span className="text-xs" style={muted}> — for {orList.source.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {text.length > 0 && (
        <div className="mt-3">
          {/* Deliberately no claim of completeness: these lines are the
              free-text residue nothing could resolve, quoted as written. */}
          <h3 className="text-sm font-semibold">Also listed, as written</h3>
          <ul className="mt-1 space-y-0.5">
            {text.map((line, i) => (
              <li key={i}>
                <span>{line.text}</span>
                {line.source && (
                  <span className="text-xs" style={muted}> — {line.source.name}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
